import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Alert, 
  Typography, 
  CircularProgress,
  Link as MuiLink,
  useTheme,
  Paper
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { Link } from 'react-router-dom';
import { AUTH_CONFIG } from '../../config';
import { APP_CONFIG } from '../../config';
import { Login as LoginIcon } from '@mui/icons-material';

function SAMLLogin() {
  const auth = useAuth() || {};
  const initiateLogin = auth.initiateLogin;
  const samlConfig = auth.samlConfig;
  const error = auth.error;
  const theme = useTheme();
  
  const [loading, setLoading] = useState(false);
  const [samlError, setSamlError] = useState('');

  useEffect(() => {
    // Check if we have an error from a SAML callback
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get('error');
    if (errorParam) {
      setSamlError(decodeURIComponent(errorParam));
    }
  }, []);

  const handleLogin = () => {
    setLoading(true);
    try {
      if (typeof initiateLogin === 'function') {
        initiateLogin();
      } else {
        setSamlError('Login functionality is not available');
        setLoading(false);
      }
    } catch (err) {
      setSamlError('Failed to initiate login. Please try again later.');
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '75vh',
        className: 'animate-fade-in'
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 400, px: 2 }}>
        <Card elevation={4} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textCenter: 'center', mb: 4, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={800} gutterBottom sx={{ letterSpacing: -1 }}>
                {APP_CONFIG.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Sign in to access your account
              </Typography>
            </Box>

            {(error || samlError) && (
              <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                {samlError || error}
              </Alert>
            )}

            {samlConfig?.enabled !== false ? (
              <Button 
                onClick={handleLogin} 
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !initiateLogin}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                sx={{ 
                  py: 1.5, 
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: '1rem'
                }}
              >
                {loading ? 'Signing in...' : `Sign in with ${samlConfig?.provider || 'Azure'} SSO`}
              </Button>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                SAML authentication is not configured.
              </Alert>
            )}

            {AUTH_CONFIG.manualLoginPath && (
              <Box sx={{ textAlign: 'center', mt: 4 }}>
                <Typography variant="caption" color="text.secondary">
                  Need direct access?
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <MuiLink 
                    component={Link} 
                    to={AUTH_CONFIG.manualLoginPath}
                    sx={{ 
                      fontWeight: 600, 
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' }
                    }}
                  >
                    Administrator Login
                  </MuiLink>
                </Box>
              </Box>
            )}
          </CardContent>
          <Box 
            sx={{ 
              py: 2, 
              textAlign: 'center', 
              bgcolor: 'action.hover',
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Typography variant="caption" color="text.disabled">
              &copy; {new Date().getFullYear()} {APP_CONFIG.company}
            </Typography>
          </Box>
        </Card>
      </Box>
    </Box>
  );
}

export default SAMLLogin;
